import { useRef, useState, type DragEvent } from "react";
import { Upload, X } from "lucide-react";
import { cn, validateFile } from "@/lib/utils";
import { appConfig } from "@/config/app";

interface MultiFileUploadFieldProps {
  files: File[];
  onFilesChange: (files: File[]) => void;
  onError: (message: string) => void;
  /** When set, extra files beyond this count are dropped. Omit for no cap. */
  maxFiles?: number;
  className?: string;
  /** When false, only show a compact drop zone; file names are listed elsewhere. */
  showFileList?: boolean;
  /** When true, drop zone and file picker are inactive. */
  disabled?: boolean;
}

export function MultiFileUploadField({
  files,
  onFilesChange,
  onError,
  maxFiles,
  className,
  showFileList = true,
  disabled = false,
}: MultiFileUploadFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const addFiles = (incoming: File[]) => {
    if (disabled || incoming.length === 0) return;

    const next: File[] = [...files];
    const skippedNames: string[] = [];
    for (const candidate of incoming) {
      const duplicate = next.some(
        (existing) =>
          existing.name === candidate.name &&
          existing.size === candidate.size &&
          existing.lastModified === candidate.lastModified
      );
      if (maxFiles != null && next.length >= maxFiles) {
        if (!duplicate) {
          skippedNames.push(candidate.name);
        }
        continue;
      }
      const result = validateFile(candidate);
      if (!result.valid) {
        onError(result.error ?? `Invalid file: ${candidate.name}`);
        continue;
      }
      if (!duplicate) {
        next.push(candidate);
      }
    }

    // Notify parent of the (possibly truncated) list first, then set the error.
    // Callers often clear errors inside onFilesChange; onError must win.
    onFilesChange(next);
    if (skippedNames.length > 0 && maxFiles != null) {
      const names = skippedNames.join(", ");
      const addedCount = next.length - files.length;
      onError(
        addedCount === 0
          ? `Maximum ${maxFiles} files per batch. Not added: ${names}`
          : `Only the first ${maxFiles} files were added. Not added: ${names}`
      );
    } else {
      onError("");
    }
  };

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (disabled) return;
    addFiles(Array.from(e.dataTransfer.files));
  };

  const removeFile = (index: number) => {
    if (disabled) return;
    onFilesChange(files.filter((_, i) => i !== index));
  };

  const emptyHint =
    maxFiles != null ? `Select or drop files (up to ${maxFiles})` : "Select or drop files";
  const moreHint =
    maxFiles != null
      ? `Click or drop to add more (${files.length}/${maxFiles})`
      : "Click or drop to add more";
  const canAddMore = maxFiles == null || files.length < maxFiles;

  return (
    <div className={cn("w-full max-w-lg", className)}>
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled || undefined}
        onClick={() => {
          if (disabled) return;
          inputRef.current?.click();
        }}
        onKeyDown={(e) => {
          if (disabled) return;
          if (e.key === "Enter") inputRef.current?.click();
        }}
        onDragOver={(e) => {
          e.preventDefault();
          if (disabled) return;
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        className={cn(
          "relative flex flex-col gap-2 rounded-lg border bg-white px-4 py-3 transition-all duration-200 min-h-[3.25rem]",
          disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer hover:shadow-sm",
          isDragging
            ? "border-[var(--color-brand-primary)] bg-[var(--color-brand-surface)] ring-2 ring-[var(--color-brand-primary)]/20"
            : disabled
              ? "border-[var(--color-border)]"
              : "border-[var(--color-border)] hover:border-[var(--color-brand-primary)]",
          files.length > 0 && !disabled && "border-[var(--color-brand-primary)]"
        )}
      >
        {!showFileList || files.length === 0 ? (
          <div className="flex items-center gap-3">
            <span className="text-[var(--color-text-tertiary)] text-sm flex-1">
              {files.length === 0 ? emptyHint : moreHint}
            </span>
            <span className="flex h-9 w-9 items-center justify-center rounded-md bg-[var(--color-button-primary-bg)] text-white shrink-0">
              <Upload className="h-4 w-4" />
            </span>
          </div>
        ) : (
          <ul className="space-y-1.5 w-full">
            {files.map((file, index) => (
              <li
                key={`${file.name}-${file.size}-${file.lastModified}`}
                className="flex items-center gap-2 text-sm"
              >
                <span className="truncate flex-1 rounded-md bg-[var(--color-bg-tertiary)] px-2 py-1">
                  {file.name}
                </span>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeFile(index);
                  }}
                  className="p-1 rounded-full hover:bg-[var(--color-bg-tertiary)] transition-colors shrink-0"
                  aria-label={`Remove ${file.name}`}
                >
                  <X className="h-4 w-4 text-[var(--color-text-secondary)]" />
                </button>
              </li>
            ))}
            {canAddMore ? (
              <li className="text-xs text-[var(--color-text-tertiary)] pt-1">{moreHint}</li>
            ) : null}
          </ul>
        )}
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={appConfig.upload.acceptedTypes.join(",")}
          disabled={disabled}
          onChange={(e) => {
            addFiles(Array.from(e.target.files ?? []));
            e.target.value = "";
          }}
          className="sr-only"
          data-testid="multi-file-input"
        />
      </div>
      {maxFiles != null ? (
        <p className="mt-1.5 text-xs text-[var(--color-text-tertiary)]">
          Up to {maxFiles} files per batch.
        </p>
      ) : null}
    </div>
  );
}
